import {
  Activity,
  Award,
  BadgePercent,
  Banknote,
  BarChart3,
  Bookmark,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  Clapperboard,
  Compass,
  CreditCard,
  Crosshair,
  Database,
  Eye,
  Film,
  Flag,
  Gift,
  Globe,
  Handshake,
  Hash,
  Heart,
  Image,
  Landmark,
  Languages,
  Layers,
  LayoutGrid,
  Lightbulb,
  Link2,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  MousePointerClick,
  Music,
  Package,
  Palette,
  PenTool,
  Percent,
  PieChart,
  Play,
  Radio,
  Rocket,
  Search,
  Send,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Speaker,
  Star,
  Store,
  Tag,
  Target,
  Ticket,
  Trophy,
  Tv,
  Users,
  Video,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * The icons a category can choose from.
 *
 * A CURATED subset, not all of lucide: the full set is thousands of glyphs, so
 * a picker over it is a worse experience than a good default, and every entry
 * shipped here is one a marketing taxonomy might plausibly want. Adding to this
 * list is the intended way to extend it.
 *
 * Keys are the lucide component names, which is exactly what the backend
 * stores — so the stored value stays readable in the database and in an API
 * response instead of being an opaque id.
 */
export const CATEGORY_ICON_REGISTRY: Record<string, LucideIcon> = {
  Activity,
  Award,
  BadgePercent,
  Banknote,
  BarChart3,
  Bookmark,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  Clapperboard,
  Compass,
  CreditCard,
  Crosshair,
  Database,
  Eye,
  Film,
  Flag,
  Gift,
  Globe,
  Handshake,
  Hash,
  Heart,
  Image,
  Landmark,
  Languages,
  Layers,
  LayoutGrid,
  Lightbulb,
  Link2,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  MousePointerClick,
  Music,
  Package,
  Palette,
  PenTool,
  Percent,
  PieChart,
  Play,
  Radio,
  Rocket,
  Search,
  Send,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Speaker,
  Star,
  Store,
  Tag,
  Target,
  Ticket,
  Trophy,
  Tv,
  Users,
  Video,
  Wallet,
  Zap,
};

/** Shown when a category has no icon, or names one we don't ship. */
export const DEFAULT_CATEGORY_ICON: LucideIcon = LayoutGrid;

export const CATEGORY_ICON_NAMES = Object.keys(CATEGORY_ICON_REGISTRY).sort();

/**
 * Resolves a stored icon name to a component.
 *
 * Never throws and never returns undefined: the name is free text from the
 * database, so an unknown or stale value has to degrade to the default rather
 * than crash a rail that is otherwise fine.
 */
export function resolveCategoryIcon(name: string | null | undefined): LucideIcon {
  if (!name) return DEFAULT_CATEGORY_ICON;
  return CATEGORY_ICON_REGISTRY[name] ?? DEFAULT_CATEGORY_ICON;
}

/**
 * Splits a PascalCase icon name into words so search matches how people type:
 * "shopping cart" and "ShoppingCart" both find `ShoppingCart`.
 */
function searchableLabel(name: string): string {
  return (
    name
      // eslint-disable-next-line i18next/no-literal-string -- regex replacement pattern
      .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
      .toLowerCase()
  );
}

/** Filters the registry by a free-text query. Empty query returns everything. */
export function searchCategoryIcons(query: string): string[] {
  const term = query.trim().toLowerCase();
  if (!term) return CATEGORY_ICON_NAMES;
  return CATEGORY_ICON_NAMES.filter(
    (name) => searchableLabel(name).includes(term) || name.toLowerCase().includes(term),
  );
}
